'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["messageId", "index", "items", "label", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChat } from "../hooks/useChat.js";
import { useMessageIds } from "../hooks/useMessage.js";
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { jsx as _jsx } from "react/jsx-runtime";
function resolveMessageIndex(messageId, index, items) {
  if (index != null) {
    return index;
  }
  return items.indexOf(messageId);
}
export const UnreadMarker = /*#__PURE__*/React.forwardRef(function UnreadMarker(props, ref) {
  const {
      messageId,
      index,
      items: itemsProp,
      label: labelProp,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const localeText = useChatLocaleText();
  const label = labelProp ?? localeText.unreadMarkerLabel;
  const defaultItems = useMessageIds();
  const {
    activeConversationId,
    conversations
  } = useChat();
  const items = itemsProp ?? defaultItems;
  const messageIndex = resolveMessageIndex(messageId, index, items);
  const activeConversation = React.useMemo(() => activeConversationId == null ? null : conversations.find(candidate => candidate.id === activeConversationId) ?? null, [activeConversationId, conversations]);
  const boundaryIndex = React.useMemo(() => {
    if (!activeConversation || items.length === 0) {
      return -1;
    }
    if (activeConversation.unreadCount != null && activeConversation.unreadCount > 0) {
      return Math.max(0, items.length - activeConversation.unreadCount);
    }
    if (activeConversation.readState === 'unread') {
      return 0;
    }
    return -1;
  }, [activeConversation, items.length]);
  const hasBoundary = boundaryIndex >= 0 && boundaryIndex === messageIndex;
  const ownerState = React.useMemo(() => ({
    messageId,
    hasBoundary,
    label
  }), [hasBoundary, label, messageId]);
  const Root = slots?.root ?? 'div';
  const Label = slots?.label ?? 'div';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref,
      role: 'separator'
    }, getDataAttributes({
      hasBoundary: ownerState.hasBoundary
    }))
  });
  const labelProps = useSlotProps({
    elementType: Label,
    externalSlotProps: slotProps?.label,
    ownerState
  });
  if (!hasBoundary) {
    return null;
  }
  return /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
    children: /*#__PURE__*/_jsx(Label, _extends({}, labelProps, {
      children: label
    }))
  }));
});
if (process.env.NODE_ENV !== "production") UnreadMarker.displayName = "UnreadMarker";